/* FUA — storefront script, loaded as a deferred module on every page.
 * Everything is progressive enhancement: forms and links work without it. */

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const motionOK = () => !matchMedia('(prefers-reduced-motion: reduce)').matches;
const config = JSON.parse($('#FuaConfig')?.textContent || '{}');
const routes = config.routes || { cart_add: '/cart/add', cart_change: '/cart/change' };
const strings = config.strings || {};

/* ---------- Dialogs: menu drawer, cart drawer, access-code modal ---------- */

function openDialog(dialog) {
  if (dialog && !dialog.open) dialog.showModal();
}

// Close with the exit animation (fua.css .is-closing), then really close.
function closeDialog(dialog) {
  if (!dialog?.open || dialog.classList.contains('is-closing')) return;
  if (!motionOK()) {
    dialog.close();
    return;
  }
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    dialog.classList.remove('is-closing');
    dialog.close();
  };
  dialog.classList.add('is-closing');
  dialog.addEventListener('animationend', finish, { once: true });
  setTimeout(finish, 400);
}

// Escape: animate out instead of vanishing.
document.addEventListener('cancel', (event) => {
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement && dialog.open && motionOK()) {
    event.preventDefault();
    closeDialog(dialog);
  }
}, true);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-open-dialog]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.openDialog);
    if (dialog) {
      event.preventDefault();
      openDialog(dialog);
      // data-focus: land on a field inside (the access code in the drawer), once it has slid up.
      const target = opener.dataset.focus && document.getElementById(opener.dataset.focus);
      if (target) setTimeout(() => target.focus(), 350);
      return;
    }
  }
  // Any link to #waitlist opens the slide-up Waitlist sheet when it's on the page; without the
  // sheet the link scrolls to the Waitlist section as usual.
  const waitlistLink = event.target.closest('a[href$="#waitlist"]');
  const sheet = waitlistLink && document.getElementById('WaitlistSheet');
  if (sheet) {
    event.preventDefault();
    openDialog(sheet);
    return;
  }
  // The hero countdown is a tap target for the drawer too (its own buttons and links keep working).
  const timer = event.target.closest('[data-open-sheet]');
  if (timer && !event.target.closest('a, button')) {
    const drawer = document.getElementById('WaitlistSheet');
    if (drawer) {
      event.preventDefault();
      openDialog(drawer);
      return;
    }
  }
  if (event.target.closest('[data-close-dialog]')) {
    closeDialog(event.target.closest('dialog'));
    return;
  }
  // A click that lands on the <dialog> itself outside its box is a backdrop click.
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement && dialog.open) {
    const box = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) closeDialog(dialog);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const timer = event.target.closest?.('[data-open-sheet]');
  if (!timer || event.target !== timer) return;
  const drawer = document.getElementById('WaitlistSheet');
  if (drawer) {
    event.preventDefault();
    openDialog(drawer);
  }
});

$$('dialog [data-open-on-load]').forEach((element) => openDialog(element.closest('dialog')));

/* ---------- Desktop dropdown menus ---------- */

const dropdowns = $$('details[data-dropdown]');
dropdowns.forEach((dropdown) => {
  dropdown.addEventListener('toggle', () => {
    if (dropdown.open) dropdowns.forEach((other) => other !== dropdown && (other.open = false));
  });
});
document.addEventListener('click', (event) => {
  dropdowns.forEach((dropdown) => {
    if (dropdown.open && !dropdown.contains(event.target)) dropdown.open = false;
  });
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  dropdowns.forEach((dropdown) => {
    if (!dropdown.open) return;
    dropdown.open = false;
    dropdown.querySelector('summary').focus();
  });
});

/* ---------- Money ---------- */

function withDelimiters(cents, decimals, thousands, decimal) {
  const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
}

function formatMoney(cents) {
  const format = (config.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(cents, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(cents, 2, "'", '.');
      case 'amount_with_space_separator':
        return withDelimiters(cents, 2, ' ', ',');
      default:
        return withDelimiters(cents, 2, ',', '.');
    }
  });
}

/* ---------- Cart ---------- */

const cartDrawer = () => document.getElementById('CartDrawer');

function updateCartCount(count) {
  $$('[data-cart-count]').forEach((badge) => {
    badge.textContent = count;
    badge.hidden = count === 0;
  });
}

function renderCartDrawer(html) {
  const drawer = cartDrawer();
  if (!drawer || !html) return;
  const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
  const current = drawer.querySelector('[data-cart-content]');
  if (!next || !current) return;
  current.replaceWith(next);
  const count = Number(next.dataset.itemCount);
  if (!Number.isNaN(count)) updateCartCount(count);
}

async function cartRequest(url, body) {
  const response = await fetch(`${url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: location.pathname }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.description || data.message || strings.cartError);
  return data;
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-open-cart]');
  if (!link || !cartDrawer()) return;
  event.preventDefault();
  openDialog(cartDrawer());
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-product-form]');
  if (!form || !cartDrawer()) return;
  event.preventDefault();
  // The form's own Add button, plus the buy bar's button that submits it from outside.
  const buttons = [...form.querySelectorAll('[data-add-button]'), ...(form.id ? $$(`[data-add-button][form="${form.id}"]`) : [])];
  const error = form.querySelector('[data-form-error]');
  const data = new FormData(form);
  buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
  if (error) error.hidden = true;
  try {
    const result = await cartRequest(routes.cart_add, {
      items: [{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }],
    });
    renderCartDrawer(result.sections?.['cart-drawer']);
    openDialog(cartDrawer());
  } catch (problem) {
    if (error) {
      error.textContent = problem.message;
      error.hidden = false;
    }
  } finally {
    buttons.forEach((button) => button.removeAttribute('aria-busy'));
  }
});

document.addEventListener('click', async (event) => {
  const control = event.target.closest('[data-line-change]');
  if (!control) return;
  event.preventDefault();
  const content = control.closest('[data-cart-content]');
  content?.setAttribute('aria-busy', 'true');
  try {
    const cart = await cartRequest(routes.cart_change, {
      line: Number(control.dataset.line),
      quantity: Number(control.dataset.quantity),
    });
    renderCartDrawer(cart.sections?.['cart-drawer']);
    updateCartCount(cart.item_count);
    cartDrawer()?.querySelector('[data-cart-content]')?.focus();
  } catch (problem) {
    const error = content?.querySelector('[data-cart-error]');
    if (error) {
      error.textContent = problem.message;
      error.hidden = false;
    }
  } finally {
    content?.removeAttribute('aria-busy');
  }
});

/* ---------- Quantity steppers ---------- */

document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-qty-step]');
  if (!step) return;
  const input = step.closest('[data-qty]')?.querySelector('input');
  if (!input) return;
  const min = Number(input.min || 1);
  const max = input.max ? Number(input.max) : Infinity;
  input.value = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(step.dataset.qtyStep)));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------- Product: gallery + variant picker ---------- */

function scrollTrackTo(track, slide) {
  if (!track || !slide) return;
  const behavior = motionOK() ? 'smooth' : 'auto';
  if (track.scrollWidth > track.clientWidth + 1) {
    const left = track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({ left, behavior });
  } else {
    slide.scrollIntoView({ block: 'center', behavior }); // desktop: the photos stack down the page
  }
}

function initGallery(gallery) {
  const track = $('[data-gallery-track]', gallery);
  if (!track) return;
  const slides = [...track.children];
  const markers = $$('[data-gallery-go]', gallery);
  if (markers.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = slides.indexOf(entry.target);
          markers.forEach((marker) =>
            marker.setAttribute('aria-current', String(Number(marker.dataset.galleryGo) === index)),
          );
        });
      },
      { root: track, threshold: 0.6 },
    );
    slides.forEach((slide) => observer.observe(slide));
  }
  gallery.addEventListener('click', (event) => {
    const marker = event.target.closest('[data-gallery-go]');
    if (marker) scrollTrackTo(track, slides[Number(marker.dataset.galleryGo)]);
  });
}

function initProduct(root) {
  const source = $('[data-product-json]', root);
  const form = $('form[data-product-form]', root) || $('form[action*="/cart/add"]', root);
  if (!source || !form) return;
  const variants = JSON.parse(source.textContent);
  const groups = $$('[data-option-index]', root);
  const idInput = $('input[name="id"]', form);
  // The Add button and the price appear twice on phones: in the form and in the buy bar.
  const buttons = $$('[data-add-button]', root);
  const labels = $$('[data-add-label]', root);
  const prices = $$('[data-product-price]', root);
  const track = $('[data-gallery-track]', root);

  // Phones: the buy bar slides up once the main Add button has scrolled off the top. Checked on
  // every scroll (one rectangle read per frame), since a jump straight past the button fires no
  // intersection event.
  const bar = $('[data-buy-bar]', root);
  const anchor = $('.product__buy', root);
  if (bar && anchor) {
    let frame = 0;
    const place = () => {
      frame = 0;
      bar.classList.toggle('is-visible', anchor.getBoundingClientRect().bottom < 0);
    };
    addEventListener('scroll', () => frame || (frame = requestAnimationFrame(place)), { passive: true });
    place();
  }

  const selectedValues = () =>
    groups.map((group) => ($('input:checked', group) || $('input', group))?.value);

  function markUnavailable(values) {
    groups.forEach((group, index) => {
      $$('input', group).forEach((input) => {
        const trial = [...values];
        trial[index] = input.value;
        const match = variants.find((variant) => variant.options.every((option, i) => option === trial[i]));
        input.closest('.pill')?.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  }

  function update() {
    const values = selectedValues();
    const variant = variants.find((candidate) => candidate.options.every((option, i) => option === values[i]));
    markUnavailable(values);
    groups.forEach((group, index) => {
      const label = $('[data-option-value]', group);
      if (label) label.textContent = values[index];
    });
    if (!variant) {
      buttons.forEach((button) => (button.disabled = true));
      labels.forEach((label) => (label.textContent = strings.unavailable));
      return;
    }
    if (idInput) idInput.value = variant.id;
    buttons.forEach((button) => (button.disabled = !variant.available));
    labels.forEach((label) => (label.textContent = variant.available ? strings.addToCart : strings.soldOut));
    const onSale = variant.compare_at_price > variant.price;
    const compare = onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '';
    prices.forEach((price) => {
      price.innerHTML = `<span class="price${onSale ? ' price--sale' : ''}">${compare}<span class="price__current">${formatMoney(variant.price)}</span></span>`;
    });
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState(history.state, '', url);
    if (variant.media) scrollTrackTo(track, $(`[data-media-id="${variant.media}"]`, root));
  }

  root.addEventListener('change', (event) => {
    if (event.target.closest('[data-option-index]')) update();
  });
  // The server already rendered the right variant, price and button state; only mark sold-out
  // options here. (Running update() on load could disable the buttons on a short option list.)
  markUnavailable(selectedValues());
}

/* ---------- Collection sorting ---------- */

document.addEventListener('change', (event) => {
  const select = event.target.closest('select[data-sort]');
  if (!select) return;
  const url = new URL(location.href);
  url.searchParams.set('sort_by', select.value);
  url.searchParams.delete('page');
  location.assign(url);
});

/* ---------- 3D tilt on product images (mouse only; touch keeps native scrolling) ---------- */

if (
  document.body.classList.contains('has-tilt') &&
  matchMedia('(hover: hover) and (pointer: fine)').matches &&
  motionOK()
) {
  let target = null;
  let frame = 0;
  let pointerX = 0;
  let pointerY = 0;

  const reset = (element) => {
    element.classList.remove('is-tilting');
    ['--tilt-x', '--tilt-y', '--glare-x', '--glare-y'].forEach((name) => element.style.removeProperty(name));
  };

  const apply = () => {
    frame = 0;
    if (!target) return;
    const box = target.getBoundingClientRect();
    const x = (pointerX - box.left) / box.width - 0.5;
    const y = (pointerY - box.top) / box.height - 0.5;
    target.style.setProperty('--tilt-x', `${(x * 14).toFixed(2)}deg`);
    target.style.setProperty('--tilt-y', `${(-y * 14).toFixed(2)}deg`);
    target.style.setProperty('--glare-x', `${((x + 0.5) * 100).toFixed(1)}%`);
    target.style.setProperty('--glare-y', `${((y + 0.5) * 100).toFixed(1)}%`);
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      const element = event.target.closest?.('[data-tilt]') || null;
      if (element !== target) {
        if (target) reset(target);
        target = element;
        target?.classList.add('is-tilting');
      }
      if (!target) return;
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!frame) frame = requestAnimationFrame(apply);
    },
    { passive: true },
  );
  document.documentElement.addEventListener(
    'pointerleave',
    () => {
      if (target) reset(target);
      target = null;
    },
    { passive: true },
  );
}

/* ---------- Product recommendations ---------- */

function initRecommendations(container) {
  const observer = new IntersectionObserver(
    async ([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      try {
        const html = await (await fetch(container.dataset.url)).text();
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (next?.querySelector('.card')) container.innerHTML = next.innerHTML;
        else container.closest('.shopify-section')?.setAttribute('hidden', '');
      } catch {
        container.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    },
    { rootMargin: '0px 0px 400px 0px' },
  );
  observer.observe(container);
}

/* ---------- Campaign video: load when near the screen, play only if motion is welcome ---------- */

function initLazyVideo(video) {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        if (!video.paused) video.pause();
        return;
      }
      if (!video.dataset.loaded) {
        $$('source[data-src]', video).forEach((source) => (source.src = source.dataset.src));
        video.load();
        video.dataset.loaded = 'true';
      }
      if (motionOK()) video.play().catch(() => {});
    },
    { rootMargin: '200px 0px' },
  );
  observer.observe(video);
}

/* ---------- Spinning 360° logo: poster first, sheet after load, paused off screen ---------- */

function initSpin(spin) {
  if (spin.dataset.ready) return;
  spin.dataset.ready = 'true';
  if (spin.classList.contains('has-poster') && motionOK()) {
    const start = () => {
      const sheet = new Image();
      const spinning = () => spin.classList.add('is-spinning');
      // Swap once the sheet can paint without a hitch; a browser that defers decoding (a background
      // tab) still swaps shortly after the bytes arrive. If the sheet fails, the poster stays.
      sheet.addEventListener('load', () => setTimeout(spinning, 250), { once: true });
      sheet.src = spin.dataset.sheet;
      sheet.decode().then(spinning, () => {});
    };
    if (document.readyState === 'complete') start();
    else addEventListener('load', start, { once: true });
  }
  new IntersectionObserver(([entry]) => spin.classList.toggle('is-paused', !entry.isIntersecting)).observe(spin);
}

/* ---------- Product quadrants: backgrounds pause off screen, videos load when near ---------- */

function initQuads(list) {
  if (list.dataset.ready) return;
  list.dataset.ready = 'true';
  const observer = new IntersectionObserver(
    (entries) => entries.forEach((entry) => entry.target.classList.toggle('is-off', !entry.isIntersecting)),
    { rootMargin: '15% 0px' },
  );
  $$('.quad', list).forEach((quad) => observer.observe(quad));
  $$('[data-quad-video] video', list).forEach(initLazyVideo);
}

/* ---------- Hero: the drawer edge at the bottom lifts the sticker floor by its own height ---------- */

function initPeek(globe) {
  if (globe.dataset.peekReady) return;
  globe.dataset.peekReady = 'true';
  const edge = $('.hero-overlay--drawer', globe);
  if (!edge) return;
  const measure = () => globe.style.setProperty('--peek-h', `${Math.round(edge.offsetHeight)}px`);
  new ResizeObserver(measure).observe(edge);
  measure();
}

/* ---------- Scroll reveals: things rise into place the first time they come into view ---------- */

// Keep in step with the selector list in fua.css (Motion). Items get a stagger index --i from
// their position among revealed siblings; with reduced motion everything shows at once.
const REVEAL = '.section-head, .product-grid > li, .tiles > li, .lookbook__track, .lookbook__grid > li, .statement > *, .waitlist__intro, .waitlist__body, .footer__inner > *, .quad, .countdown--panel, .countdown--strip';

function initReveal(root) {
  const targets = $$(REVEAL, root).filter((element) => !element.dataset.reveal);
  if (!targets.length) return;
  targets.forEach((element) => (element.dataset.reveal = 'true'));
  if (!motionOK() || !('IntersectionObserver' in window)) {
    targets.forEach((element) => element.classList.add('is-in'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }),
    { rootMargin: '0px 0px -8% 0px', threshold: 0.1 },
  );
  targets.forEach((element) => {
    const siblings = [...(element.parentElement?.children || [])].filter((child) => child.matches(REVEAL));
    element.style.setProperty('--i', String(Math.min(Math.max(siblings.indexOf(element), 0), 7)));
    observer.observe(element);
  });
}

/* ---------- Lookbook: swipe through on the homepage ---------- */

// The row scrolls sideways on its own (fingers, trackpads, arrow keys once it has focus). On top
// of that: the arrows step one photo, a mouse can drag the row, and the line and count under it
// follow along. The click that ends a drag doesn't open the photo it lands on.
function initLookbook(section) {
  const track = $('[data-look-track]', section);
  if (!track || track.dataset.ready) return;
  track.dataset.ready = 'true';
  const prev = $('[data-look-prev]', section);
  const next = $('[data-look-next]', section);
  const thumb = $('[data-look-thumb]', section);
  const current = $('[data-look-current]', section);
  const photos = $$('[data-look-photo]', track).length;
  const glide = () => (motionOK() ? 'smooth' : 'auto');
  const maxScroll = () => Math.max(track.scrollWidth - track.clientWidth, 0);

  // Where the row stops for each slide: the first slide snaps at 0 and the others follow at their
  // distance from it (the row's side padding and its snap padding are the same --edge).
  const stops = () => {
    const slides = [...track.children];
    const max = maxScroll();
    return slides.map((slide) => Math.min(Math.max(slide.offsetLeft - slides[0].offsetLeft, 0), max));
  };
  const step = (direction) => {
    const left = track.scrollLeft;
    const list = stops();
    const target = direction > 0 ? list.find((stop) => stop > left + 2) : list.findLast((stop) => stop < left - 2);
    track.scrollTo({ left: target ?? (direction > 0 ? maxScroll() : 0), behavior: glide() });
  };
  prev?.addEventListener('click', () => step(-1));
  next?.addEventListener('click', () => step(1));

  // The line, the count and the arrows, once a frame while the row moves.
  let frame = 0;
  const update = () => {
    frame = 0;
    const left = track.scrollLeft;
    const max = maxScroll();
    const list = stops();
    const visible = Math.min(track.clientWidth / track.scrollWidth, 1);
    let index = list.reduce((best, stop, i) => (Math.abs(stop - left) < Math.abs(list[best] - left) ? i : best), 0);
    if (left >= max - 2) index = photos - 1;
    section.classList.toggle('is-static', max <= 2);
    if (prev) prev.disabled = left <= 2;
    if (next) next.disabled = left >= max - 2;
    if (thumb) {
      thumb.style.width = `${visible * 100}%`;
      thumb.style.transform = `translateX(${max > 0 ? (left / max) * (1 / visible - 1) * 100 : 0}%)`;
    }
    if (current) current.textContent = String(Math.max(Math.min(index, photos - 1), 0) + 1).padStart(2, '0');
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };
  track.addEventListener('scroll', schedule, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(track);
  update();

  // Mouse drag: the row follows the pointer, then glides to the nearest photo (at least one on,
  // once the drag has gone past a small threshold).
  let drag = null;
  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    drag = { id: event.pointerId, x: event.clientX, start: track.scrollLeft, moved: false };
  });
  track.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      track.classList.add('is-dragging');
      try {
        track.setPointerCapture(drag.id);
      } catch {}
    }
    track.scrollLeft = drag.start - dx;
  });
  const release = (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const { moved, start } = drag;
    drag = null;
    if (!moved) return;
    const left = track.scrollLeft;
    const list = stops();
    let target = list.reduce((best, stop) => (Math.abs(stop - left) < Math.abs(best - left) ? stop : best), 0);
    if (Math.abs(target - start) < 2 && Math.abs(left - start) > 40) {
      target = (left > start ? list.find((stop) => stop > start + 2) : list.findLast((stop) => stop < start - 2)) ?? target;
    }
    // Snapping comes back once the glide has landed (unless a new drag has begun).
    const settle = () => {
      if (!drag) track.classList.remove('is-dragging');
    };
    track.addEventListener('scrollend', settle, { once: true });
    setTimeout(settle, 700);
    track.scrollTo({ left: target, behavior: glide() });
    // The click that ends the drag lands on a photo: keep it from opening the photo's link.
    const swallow = (click) => {
      click.preventDefault();
      click.stopPropagation();
    };
    track.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 0);
  };
  track.addEventListener('pointerup', release);
  track.addEventListener('pointercancel', release);
  track.addEventListener('dragstart', (event) => event.preventDefault());
}

/* ---------- Waitlist pop-up: once per browser, after a delay, never over another dialog ---------- */

function initPopup(dialog) {
  if (dialog.dataset.ready) return;
  dialog.dataset.ready = 'true';
  if (window.Shopify?.designMode) return; // in the theme editor it opens when its section is selected
  const key = 'fua-popup-seen';
  const days = Number(dialog.dataset.days || 30);
  let seen = 0;
  try {
    seen = Number(localStorage.getItem(key) || 0);
  } catch {}
  if (seen && Date.now() - seen < days * 86400000) return;
  const remember = () => {
    try {
      localStorage.setItem(key, String(Date.now()));
    } catch {}
  };
  dialog.addEventListener('close', remember, { once: true });
  // If another dialog is up when the time comes (the cart, the menu), try again a little later.
  const attempt = () => {
    if ($('dialog[open]')) {
      setTimeout(attempt, 1000);
      return;
    }
    remember();
    openDialog(dialog);
  };
  setTimeout(attempt, Number(dialog.dataset.delay || 5) * 1000);
}

/* ---------- Header: a shadow once the page has scrolled ---------- */

const header = $('.header');
if (header) {
  const onScroll = () => header.classList.toggle('is-scrolled', scrollY > 24);
  addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ---------- Init (and re-init when the theme editor reloads a section) ---------- */

function init(root = document) {
  try {
    initReveal(root);
  } catch {
    $$(REVEAL, root).forEach((element) => element.classList.add('is-in'));
  }
  $$('.globe--peek', root).forEach(initPeek);
  $$('[data-gallery]', root).forEach(initGallery);
  $$('[data-product]', root).forEach(initProduct);
  $$('[data-recommendations][data-url]', root).forEach(initRecommendations);
  $$('video[data-lazy-video], .globe__video video', root).forEach(initLazyVideo);
  $$('[data-spin]', root).forEach(initSpin);
  $$('[data-quads]', root).forEach(initQuads);
  $$('[data-lookbook]', root).forEach(initLookbook);
  $$('dialog[data-popup]', root).forEach(initPopup);
}

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
// Theme editor: selecting the pop-up or drawer section shows it; deselecting hides it again.
document.addEventListener('shopify:section:select', (event) => {
  const dialog = event.target.querySelector?.('dialog[data-popup], dialog#WaitlistSheet');
  if (dialog) openDialog(dialog);
});
document.addEventListener('shopify:section:deselect', (event) => {
  const dialog = event.target.querySelector?.('dialog[data-popup], dialog#WaitlistSheet');
  if (dialog?.open) dialog.close();
});
// Theme editor: selecting a lookbook photo brings it into the row.
document.addEventListener('shopify:block:select', (event) => {
  if (event.target.parentElement?.matches('[data-look-track]')) {
    event.target.scrollIntoView({ behavior: motionOK() ? 'smooth' : 'auto', block: 'nearest', inline: 'start' });
  }
});
